/*eslint-disable*/
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import * as url from "helper/UrlHelper";
import { getData, putData } from "utils/Gateway";
import { assetImages } from "constants";

// Main popup component for viewing list details
const ViewListDetailsPopup = ({
  afterDeleteItem = () => {},
  listId = null,
  setListId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // State variables
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [listTitle, setListTitle] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [leadList, setLeadList] = useState([]);
  const [deletingItemId, setDeletingItemId] = useState(null); // Track which item is being deleted

  // Fetch list details from API
  const getlistdetails = async () => {
    try {
      setIsLoadingDetails(true);
      let requestUrl =
        url.API_BASE_URL +
        url.API_LIST_DETAILS_WITH_ITEMS +
        `/${listId}?token=${token}`;
      const response = await getData(requestUrl);

      setIsLoadingDetails(false);

      // If response is successful, update state with list details
      if (response.status) {
        setListTitle(response.data.title);
        setCompanyList(response.data.companies);
        setContactList(response.data.contacts);
        setLeadList(response.data.leads);
      }
    } catch (error) {
      console.log("Error fetching list details:", error);
    }
  };

  // Delete an item from the list (company/contact/lead)
  const deleteItemFromList = async (itemId) => {
    try {
      setDeletingItemId(itemId); // Show spinner for this item
      const apiDate = {
        itemid: itemId,
      };

      let requestUrl =
        url.API_BASE_URL +
        url.API_DELETE_ITEM_FROM_LIST +
        `/${listId}?token=${token}`;

      const response = await putData(requestUrl, apiDate);

      // Remove item from the correct list in state
      if (response.status) {
        if (companyList.find((item) => item._id === itemId)) {
          setCompanyList(companyList.filter((item) => item._id !== itemId));
        }
        if (contactList.find((item) => item._id === itemId)) {
          setContactList(contactList.filter((item) => item._id !== itemId));
        }
        if (leadList.find((item) => item._id === itemId)) {
          setLeadList(leadList.filter((item) => item._id !== itemId));
        }
        afterDeleteItem([response.data]);
      }
      setDeletingItemId(null); // Hide spinner
    } catch (error) {
      setDeletingItemId(null); // Hide spinner on error
      console.log("Error deleting item from list:", error);
    }
  };

  // Reset popup state when closed
  const resetHandler = () => {
    setListTitle("");
    setCompanyList([]);
    setContactList([]);
    setLeadList([]);
    setListId(null);
  };

  // Render table rows for a given list type
  const renderListRows = (list, type) =>
    list.map((item, index) => (
      <tr key={index}>
        <td className="border-bottom-0 p-1">
          <span className="form-control bg-white shadow-none border-gray-300">
            {type === "company" ? item.name : item.fullname}
          </span>
        </td>
        <td className="border-bottom-0 p-1">
          <span className="form-control bg-white shadow-none border-gray-300">
            {item.email}
          </span>
        </td>
        <td className="action border-bottom-0">
          <div
            className="avatar rounded-circle overflow-hidden"
            style={{ width: "25px", height: "25px" }}
          >
            <img
              src={
                item.image == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + item.image
              }
              alt="admin"
              className="w-100 h-100 object-fit-cover object-center"
            />
          </div>
        </td>
        <td className="action border-bottom-0">
          {deletingItemId === item._id ? (
            // Show spinner while deleting
            <div
              className="spinner-border text-danger"
              role="status"
              style={{ width: "1.5rem", height: "1.5rem" }}
            >
              <span className="visually-hidden">{t("Deleting...")}</span>
            </div>
          ) : (
            // Delete button
            <button
              className="text-danger border-0 bg-transparent"
              onClick={() => deleteItemFromList(item._id)}
            >
              <span className="d-block material-symbols-outlined">delete</span>
            </button>
          )}
        </td>
      </tr>
    ));

  // Fetch list details when listId changes
  useEffect(() => {
    if (listId) {
      getlistdetails();
    }
  }, [listId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="viewListDetails"
      aria-labelledby="viewListDetailsLabel"
    >
      {/* Header section */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              list_alt
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasLabelDetailsLabel"
            >
              {t("View List Details")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {t("Details for the list item")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4">
        {/* Show loading spinner overlay while fetching details */}
        {isLoadingDetails && (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading list details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading list details...")}
            </p>
          </div>
        )}
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          {/* List title and item count */}
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-3">
            <span className="material-symbols-outlined text-primary fs-4 me-1">
              list_alt
            </span>
            <h5 className="fw-semibold mb-0 me-1">{listTitle}</h5>
            <span
              className="badge rounded-pill bg-primary text-white fs-sm px-3 py-2 shadow-sm"
              title={t("Total items in list")}
            >
              {companyList.length + contactList.length + leadList.length}
            </span>
          </div>
          {/* Table of items */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column"
          >
            <div
              className="fields-container flex-fill"
              style={{ height: "calc(100vh - 250px)", overflow: "auto" }}
            >
              <div className="table-wrapper pb-5 mb-5 ">
                <div className="table-responsive mb-1">
                  <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                    <thead>
                      <tr>
                        <th
                          width="30%"
                          className="bg-transparent fs-md fw-semibold border-0"
                        >
                          {t("Name")}
                        </th>
                        <th
                          width="40%"
                          className="bg-transparent fs-md fw-semibold border-0"
                        >
                          {t("Email")}
                        </th>
                        <th className="bg-transparent fs-md fw-semibold border-0">
                          {t("Image")}
                        </th>
                        <th className="bg-transparent fs-md fw-semibold border-0">
                          {t("Delete")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Render rows for each list type */}
                      {renderListRows(companyList, "company")}
                      {renderListRows(contactList, "contact")}
                      {renderListRows(leadList, "lead")}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ViewListDetailsPopup;
